"use client";
import InputField from "@/components/ui/inputs/InputField";
import SelectInput from "@/components/ui/inputs/SelectInput";
import Link from "next/link";
import { useState } from "react";

type Voucher = {
  title?: string;
  image?: { url?: string };
};

type VoucherFormProps = {
  vouchers: Voucher[];
};

export default function VoucherForm({ vouchers = [] }: VoucherFormProps) {
  const [selectedVoucher, setSelectedVoucher] = useState(0);
  const safeSelectedVoucher =
    vouchers.length > 0 && selectedVoucher < vouchers.length
      ? selectedVoucher
      : 0;

  const [formData, setFormData] = useState({
    message: "",
    amount: "",
    subject: "",
    email: "",
    name: "",
    address: "",
    invoice_name: "",
    invoice_address: "",
    invoice_email: "",
    invoice_note: "",
  });

  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >,
  ) => {
    const { name, value } = e.target;
    let newValue = value;

    if (name === "amount") {
      newValue = value.replace(/[^0-9]/g, "");
      if (newValue) newValue = `${newValue} €`;
    }

    setFormData((prev) => ({ ...prev, [name]: newValue }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (loading) return;
    setLoading(true);

    try {
      // 1️⃣ Save to Kirby CMS
      const cmsRes = await fetch("https://cms.travelalvaro.sk/form/vouchers", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...formData,
          selectedVoucher: vouchers?.[safeSelectedVoucher]?.title || null,
          selectedVoucherImage:
            vouchers?.[safeSelectedVoucher]?.image?.url || null,
        }),
      });

      const cmsData = await cmsRes.json();

      if (cmsData.status !== "ok") {
        throw new Error("CMS submission failed");
      }

      // 2️⃣ Send emails via Next.js API
      await fetch("/api/form/vouchers", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...formData,
          selectedVoucher: vouchers?.[safeSelectedVoucher]?.title || null,
          selectedVoucherImage:
            vouchers?.[safeSelectedVoucher]?.image?.url || null,
        }),
      });

      setSubmitted(true);
    } catch (error) {
      console.error("Error submitting voucher form:", error);
      alert("Chyba pri odosielaní formulára. Skúste to prosím znovu.");
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className='mt-6 text-center'>
        <h2 className='text-2xl font-bold mb-2'>Ďakujeme!</h2>
        <p>
          Vaša objednávka bola úspešne odoslaná.
          <br /> Budeme Vás kontaktovať.
        </p>
      </div>
    );
  }

  return (
    <div className='mb-20 text-left rounded-xl w-full max-w-4xl mx-auto'>
      {vouchers?.length > 0 && (
        <div className='flex flex-col items-center justify-center mb-6'>
          {selectedVoucher !== null && (
            <div className='w-full mx-auto max-w-2xl mb-4'>
              <img
                src={vouchers[safeSelectedVoucher].image?.url}
                alt={vouchers[safeSelectedVoucher].title}
                className='w-auto md:h-100 mx-auto rounded-xl shadow-md'
              />
            </div>
          )}
          <div className='flex flex-wrap justify-center gap-1.5 md:gap-3'>
            {vouchers.map((voucher: Voucher, index: number) => (
              <div
                key={index}
                onClick={() => setSelectedVoucher(index)}
                className={`cursor-pointer border-4 rounded-xl overflow-hidden transition ${
                  selectedVoucher === index
                    ? "border-[#619F1C]"
                    : "border-transparent opacity-70 hover:opacity-100"
                }`}
              >
                <img
                  src={voucher.image?.url}
                  alt={voucher.title}
                  className='w-18 h-18 object-cover md:w-32 md:h-32'
                />
              </div>
            ))}
          </div>
        </div>
      )}
      <form onSubmit={handleSubmit}>
        <div className='grid grid-cols-1 md:grid-cols-2 gap-4 bg-[#F5F5F5] rounded-xl p-5'>
          <div className='flex flex-col md:col-span-2'>
            <InputField
              label='Venovanie'
              name='message'
              type='textarea'
              value={formData.message}
              onChange={handleChange}
              required
              rows={5}
              placeholder='Zadaj text správy'
            />
          </div>
          <div className='flex flex-col'>
            <InputField
              label='Suma'
              name='amount'
              type='text'
              value={formData.amount}
              onChange={handleChange}
              required
              placeholder='Zadaj sumu poukážky'
            />
          </div>

          <div className='flex flex-col'>
            <SelectInput
              label='Spôsob doručenia'
              name='subject'
              value={formData.subject}
              onChange={handleChange}
              required
              placeholder='Vyber spôsob doručenia'
              options={[
                { value: "Poštou domov", label: "Poštou domov" },
                { value: "E-mail", label: "E-mail" },
              ]}
            />
          </div>

          {formData.subject === "E-mail" && (
            <div className='flex flex-col col-span-2'>
              <InputField
                label='Email, na ktorý zašleme poukážku'
                name='email'
                type='email'
                value={formData.email}
                onChange={handleChange}
                required
                placeholder='Zadaj email na ktorý zašleme vybranú poukážku'
              />
            </div>
          )}

          {formData.subject === "Poštou domov" && (
            <>
              <div className='flex flex-col '>
                <InputField
                  label='Celé meno'
                  name='name'
                  type='text'
                  value={formData.name}
                  onChange={handleChange}
                  required
                  placeholder='Zadaj svoje celé meno'
                />
              </div>
              <div className='flex flex-col '>
                <InputField
                  label='Adresa, na ktorú zašleme poukážku'
                  name='address'
                  type='text'
                  value={formData.address}
                  onChange={handleChange}
                  required
                  placeholder='Zadaj adresu na ktorú zašleme vybranú poukážku'
                />
              </div>
            </>
          )}
        </div>
        {/* Invoice info */}
        <div className='grid grid-cols-1 mt-4 md:grid-cols-2 gap-4 bg-[#F5F5F5] rounded-xl p-5 pb-3'>
          <span className='col-span-2 -mb-2 font-bold'>Fakturačné údaje</span>
          <div className='flex flex-col'>
            <InputField
              label='Celé meno'
              name='invoice_name'
              type='text'
              value={formData.invoice_name || ""}
              onChange={handleChange}
              required
              placeholder='Zadaj svoje celé meno'
            />
          </div>

          <div className='flex flex-col'>
            <InputField
              label='Adresa'
              name='invoice_address'
              type='text'
              value={formData.invoice_address || ""}
              onChange={handleChange}
              required
              placeholder='Zadaj svoju adresu'
            />
          </div>

          <div className='flex flex-col'>
            <InputField
              label='Email'
              name='invoice_email'
              type='email'
              value={formData.invoice_email || ""}
              onChange={handleChange}
              required
              placeholder='Zadaj svoj email'
            />
          </div>

          <div className='flex flex-col'>
            <InputField
              label='Poznámka'
              name='invoice_note'
              type='textarea'
              rows={2}
              value={formData.invoice_note || ""}
              onChange={handleChange}
              placeholder='Zadaj poznámku'
            />
          </div>
          <span className='font-medium text-[#A3A3A3] col-span-2 text-center'>
            Údaje pro potreby vystavenia faktúry, nebudú uvedené na poukážke
          </span>
        </div>
        <div className='flex flex-col items-center gap-4 text-xs justify-center mt-4'>
          <div className='flex items-start gap-2 text-xs md:text-center lg:justify-start'>
            <input
              type='checkbox'
              onInvalid={(e) =>
                (e.currentTarget as HTMLInputElement).setCustomValidity(
                  "Prosím, zaškrnite súhlas, ak chcete pokračovať v objednávke.",
                )
              }
              onInput={(e) =>
                (e.currentTarget as HTMLInputElement).setCustomValidity("")
              }
              required
              className='appearance-none h-4 w-4 border border-gray-400 bg-white rounded-full checked:bg-[#00638A] checked:border-white checked:border-[3px] checked:outline-[#00638A] checked:outline-1 shrink-0'
            />
            <label htmlFor='terms' className='text-left'>
              Súhlasím so spracovaním{" "}
              <Link href='/dokumenty-na-stiahnutie' className=''>
                <span className='underline text-orange-400'>
                  osobných údajov
                </span>{" "}
                a
                <span className='underline text-orange-400'>
                  {" "}
                  obchodnými podmienkami
                </span>
                .
              </Link>
            </label>
          </div>
          <button
            type='submit'
            disabled={loading}
            aria-busy={loading}
            className='bg-[#619F1C] text-white cursor-pointer text-sm py-3 rounded-full font-semibold hover:bg-[#c65c1e] transition w-38 disabled:opacity-60 disabled:cursor-not-allowed'
          >
            {loading ? (
              <span className='flex items-center justify-center gap-2'>
                <svg
                  className='animate-spin h-4 w-4 text-white'
                  viewBox='0 0 24 24'
                >
                  <circle
                    className='opacity-25'
                    cx='12'
                    cy='12'
                    r='10'
                    stroke='currentColor'
                    strokeWidth='4'
                  ></circle>
                  <path
                    className='opacity-75'
                    fill='currentColor'
                    d='M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z'
                  ></path>
                </svg>
                Odosielam...
              </span>
            ) : (
              "Odoslať"
            )}
          </button>
        </div>
      </form>
    </div>
  );
}
